'use client';

import { AlertCircle, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  EntitlementDecision,
  EntitlementReason,
} from '@/lib/types/entitlements.types';

interface FreePlanRestrictionModalProps {
  open: boolean;
  onClose: () => void;
  entitlement: EntitlementDecision;
  operation: string;
  /** The catalogue's label for the feature, e.g. "Export to CSV". */
  featureLabel?: string;
}

/**
 * What each of the service's denial reasons means to the person reading it.
 *
 * The raw strings are camelCase machine values (`notInPlan`, `manualDenied`)
 * that used to be rendered directly — a `.replace(/_/g, ' ')` on a string with
 * no underscores in it, which showed customers the enum. They stay useful in a
 * support conversation, so the reason is still named, but underneath a sentence
 * that says what actually happened.
 */
const REASON_COPY: Record<EntitlementReason, string> = {
  granted: 'This feature is available on your account.',
  notCatalogued: 'This feature is not currently gated.',
  accountDisabled: 'This account has been disabled. Support can reactivate it.',
  noPlan:
    'This account is not subscribed to a plan yet, so paid features are not available.',
  notInPlan: 'Your current plan does not include this feature.',
  planDenied: 'This feature is switched off on your current plan.',
  manualDenied: 'This feature has been switched off specifically for your account.',
  limitReached: 'You have reached your plan allowance for this.',
};

/**
 * Shown when a user tries to perform an action but doesn't have the
 * entitlement for it.
 *
 * This is different from the credit guard modal: it's not a money question,
 * it's a plan access question. The account genuinely doesn't have permission
 * to do this, and needs to upgrade or contact support.
 */
export function FreePlanRestrictionModal({
  open,
  onClose,
  entitlement,
  operation,
  featureLabel,
}: FreePlanRestrictionModalProps) {
  const supportEmail = 'support@infynd.co.in';
  const name = featureLabel || operation;
  const subject = `Request access to ${name}`;
  const isLimit = entitlement.reason === 'limitReached';

  const handleContactSupport = () => {
    window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            {isLimit ? 'Plan limit reached' : 'Feature not available'}
          </DialogTitle>
          <DialogDescription className="pt-1">
            {REASON_COPY[entitlement.reason]}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">
          <p className="text-muted-foreground">
            Feature: <span className="font-medium text-foreground">{name}</span>
          </p>
          {/* Only meaningful for a ceiling, and `limit: null` is unlimited
              rather than zero - a limit modal for an unlimited feature would
              be a bug, so it simply renders nothing. */}
          {isLimit && entitlement.limit !== null ? (
            <p className="text-muted-foreground">
              Allowance:{' '}
              <span className="font-medium text-foreground">
                {(entitlement.used ?? 0).toLocaleString()} of{' '}
                {entitlement.limit.toLocaleString()} used
              </span>
            </p>
          ) : null}
          {entitlement.planKey ? (
            <p className="text-muted-foreground">
              Plan: <span className="font-medium text-foreground capitalize">{entitlement.planKey}</span>
            </p>
          ) : null}
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-muted-foreground dark:border-blue-500/30 dark:bg-blue-500/10">
          <Headphones className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
          <p>
            Reach out to our support team. They can help you upgrade your plan or resolve
            any account restrictions.
          </p>
        </div>

        <DialogFooter className="gap-3 sm:gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button className="flex-1 font-semibold" onClick={handleContactSupport}>
            Contact Support
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FreePlanRestrictionModal;
